// ─── SUPABASE CLIENT ──────────────────────────────────────────────────────────
import { createClient } from '@supabase/supabase-js';
import { API_BASE } from './apiBase';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;

if (!SUPABASE_URL || !SUPABASE_KEY) {
  console.warn('Supabase env vars not set — localStorage fallback mode');
}

export const supabase = SUPABASE_URL && SUPABASE_KEY
  ? createClient(SUPABASE_URL, SUPABASE_KEY)
  : null;

export const isSupabaseReady = () => !!supabase;

// ─── SIGN-IN, THROUGH THE SERVER SO A LOCKOUT HAS SOMEWHERE TO LIVE ──────────
//
// The app used to call supabase.auth.signInWithPassword() straight from the browser.
// That left nowhere for account lockout to be enforced: the right place is Supabase
// Auth's Password Verification Attempt hook, which is not available on this project.
//
// So sign-in goes to our own API, which checks the lock, has Supabase verify the
// password, records the outcome, and returns the session for us to install here.
//
// SHAPED LIKE signInWithPassword ON PURPOSE — { data, error } — so the call sites did
// not have to be rewritten around a new contract. A different shape at two call sites
// is two chances to handle the error differently, and the lockout message is the one
// error that must reach the user verbatim.
const LOGIN_API = `${API_BASE}/api/auth/login`;

export async function signInThroughGate(email, password) {
  let res;
  try {
    res = await fetch(LOGIN_API, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: String(email || "").trim(), password: String(password || "") }),
    });
  } catch (e) {
    // NO FALLBACK TO DIRECT SIGN-IN, and that is the whole point.
    //
    // Falling back when this request fails would make the lockout worthless: anyone can
    // make this one request fail for themselves — block it in devtools — and be handed
    // the unguarded path. A control with an opt-out is not a control.
    //
    // It costs nothing in availability. REACT_APP_API_URL is the same host as
    // SURVAI_ENGINE_API and the app reads from it in fifty places, so if it is
    // unreachable the application does not work anyway. This adds no new single point
    // of failure; it just makes an existing one visible at the login screen.
    return { data: null, error: { message: "Sign-in is temporarily unavailable. Please try again in a moment." } };
  }

  let body = null;
  try { body = await res.json(); } catch { /* a non-JSON error page */ }

  if (!res.ok) {
    return {
      data: null,
      // The server's own wording, unwrapped. On 423 that sentence tells the person the
      // account is locked and what to do about it, and it has to survive the trip.
      error: {
        message: body?.error || (res.status === 423 ? "This account is temporarily locked." : "Incorrect email or password."),
        status: res.status,
        lockedUntil: body?.lockedUntil ?? null,
      },
    };
  }

  const session = body?.session || null;
  if (!session?.access_token || !session?.refresh_token) {
    return { data: null, error: { message: "Sign-in did not return a session. Please try again." } };
  }
  if (!supabase) return { data: null, error: { message: "Supabase not configured" } };

  // INSTALLED HERE, so everything downstream — RLS reads, realtime, the engine's JWT —
  // behaves exactly as it did when the browser signed in directly. The only thing that
  // changed is who verified the password.
  const { error: setErr } = await supabase.auth.setSession({
    access_token: session.access_token,
    refresh_token: session.refresh_token,
  });
  if (setErr) return { data: null, error: { message: setErr.message } };

  return { data: { user: body?.user ?? session.user ?? null, session }, error: null };
}

// ─── RE-AUTHENTICATION, COUNTED, WITHOUT TOUCHING THE LIVE SESSION ───────────
//
// Settings → Change Password asks for the current password before changing it, and used
// to prove it by calling supabase.auth.signInWithPassword() right here in the browser.
// That had two faults:
//
//   * NO ATTEMPT LIMIT. Somebody at an unlocked laptop could sit in that box guessing as
//     long as they liked, while the login screen next door stopped after five. An
//     attacker uses the weaker door.
//   * IT REPLACED THE LIVE SESSION with a fresh one, mid-change. If the update then
//     failed, the user was left holding a session they never started.
//
// /api/auth/verify-password fixes both: the server verifies against the account in the
// CALLER'S OWN TOKEN (there is no email parameter — one would make it an oracle for
// testing anybody's password), counts the attempt against the same lockout as sign-in,
// throws the session it had to mint away, and answers yes or no.
const VERIFY_API = `${API_BASE}/api/auth/verify-password`;

/**
 * Check the signed-in user's current password.
 *
 * Returns { ok } on success, or { ok: false, message, status }. The server's own wording
 * is passed through: on 423 that sentence is what tells the person the account is now
 * locked and who can unlock it, and "Current password is incorrect" would bury it.
 */
export async function verifyCurrentPassword(password) {
  if (!supabase) return { ok: false, message: "Supabase not configured" };

  const { data: sess } = await supabase.auth.getSession();
  const token = sess?.session?.access_token;
  // NO FALLBACK TO A DIRECT signInWithPassword, for the same reason sign-in has none: a
  // fallback anybody can trigger by making one request fail is an opt-out, and a control
  // with an opt-out is not a control.
  if (!token) return { ok: false, message: "Your session has expired. Sign in again." };

  let res;
  try {
    res = await fetch(VERIFY_API, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ password: String(password || "") }),
    });
  } catch (e) {
    return { ok: false, message: "Could not reach the server to check your password. Try again in a moment." };
  }

  let body = null;
  try { body = await res.json(); } catch { /* a non-JSON error page */ }

  if (!res.ok) {
    return {
      ok: false,
      message: body?.error || (res.status === 423 ? "This account is locked." : "Current password is incorrect."),
      status: res.status,
      lockedUntil: body?.lockedUntil ?? null,
    };
  }
  return { ok: body?.ok === true };
}
